/**
 * CRM App Component
 *
 * Lead CRM module root. Integrated into SiteOperations.io — uses the shared
 * AuthContext. ProtectedRoute mirrors the platform pattern: super_admins bypass;
 * everyone else needs the `crm` tool available via /api/my-tools. CRMProvider
 * lives in App.tsx so state persists across navigation.
 *
 * Shell layout: slim sticky app bar (with a mobile hamburger), then a flex row
 * of the collapsible <Sidebar> (desktop column / mobile drawer) and the routed
 * page. IA: / = Today worklist, /inbox = lead triage, /active + /awaiting =
 * conversation views, /by-study = kanban, /database = full patient list, and
 * /settings/* = the config hub (studies, forms, flows, templates, AI prompts,
 * release wording, system) — old top-level paths redirect there.
 * SoftphoneBar mounts once so calls survive CRM navigation.
 *
 * Typography: the CRM renders in self-hosted Inter (imported below, applied
 * via the `font-inter` wrapper) without affecting the other platform modules.
 */
import { Component, ErrorInfo, ReactNode, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { ArrowLeft, Menu } from 'lucide-react';
import '@fontsource-variable/inter';
import { useAuth } from '../contexts/AuthContext';
import { ThemeToggle } from '../theme/ThemeToggle';
import api from '../services/api';
import { ConnectionStatus, Contacts, ContactDetail, Conversations, ByStudy, Today, Calendar, StudiesList, StudyDetail, Reports, FormsList, FormBuilder, FlowsList, FlowBuilder, FaxConsole, PhoneConsole, PromptsList, PromptEditor, GlobalTemplates, ReleaseContent, RegistryBackfill, Help } from './pages';
import EligibilityReviewResults from './pages/EligibilityReviewResults';
import { SoftphoneProvider, useSoftphone } from './contexts/SoftphoneContext';
import { CallWorkspaceProvider } from './contexts/CallWorkspaceContext';
import { SoftphoneBar, ParkedCallsPanel } from './components';
import RatingPromptHost from './components/RatingPromptHost';
import CallbackReminderHost from './components/CallbackReminderHost';
import InboundSmsToastHost from './components/InboundSmsToastHost';
import CallWorkspace from './components/callmode/CallWorkspace';
import Sidebar from './components/Sidebar';
import CrmMark from './components/CrmMark';
import SettingsLayout from './pages/settings/SettingsLayout';
import { rememberLeadOrigin } from './lib';

// Keep a CRM render error from white-screening the rest of the app shell.
class CRMErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[CRM] render error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="text-center p-8 max-w-md">
            <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Something went wrong in the CRM.</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              Try reloading. The rest of the platform is unaffected.
            </p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function Spinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { session, user } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    const check = async () => {
      // Super admins always have access (tool is "Coming Soon" to others in Phase 1)
      if (user?.role === 'super_admin') {
        setAllowed(true);
        setChecking(false);
        return;
      }
      try {
        const res = await api.get('/api/my-tools');
        const crmTool = (res.data || []).find((t: any) => t.tool_id === 'crm');
        setAllowed(crmTool?.is_available === true);
      } catch (err) {
        console.error('[CRM] entitlement check failed:', err);
        setAllowed(false);
      } finally {
        setChecking(false);
      }
    };
    if (session) {
      check();
    } else {
      setChecking(false);
    }
  }, [session, user?.role]);

  if (checking) return <Spinner />;
  if (!session) return <Navigate to="/login" replace />;

  if (!allowed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center p-8 max-w-md">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Access Denied</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            You do not have access to the CRM. Please contact your administrator if you believe this is an error.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-150"
          >
            Return to Tool Hub
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

function CRMLayout({ children }: { children: ReactNode }) {
  const { inCall, hangup } = useSoftphone();
  const navigate = useNavigate();
  // One modal serves both exits (logo → hard reload to /crm, Tool Hub → /).
  // `leaveTo !== null` is the "show modal" condition; it holds the pending target.
  const [leaveTo, setLeaveTo] = useState<string | null>(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Logo home: force a full-page load (remounts SoftphoneProvider + CRMProvider,
  // refetches the Today worklist). Guard the active call with the confirm modal.
  const goHard = (to: string) => {
    if (inCall) {
      setLeaveTo(to);
    } else {
      window.location.assign(to);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Slim single-line app bar (h-12): sticky so navigation is always in
          reach without eating the vertical space the old two-line banner did.
          The sidebar's sticky offset and the shell height calc both assume
          this 3rem height — keep them in sync. */}
      <header className="sticky top-0 z-30 bg-gradient-to-r from-primary-600 to-primary-700">
        <div className="flex h-12 items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex min-w-0 items-center gap-1.5">
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              aria-label="Open navigation menu"
              className="lg:hidden -ml-2 inline-flex min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 sm:h-9 sm:w-9 items-center justify-center rounded-lg text-white/90 transition-colors duration-150 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/60"
            >
              <Menu className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => goHard('/crm')}
              aria-label="Go to CRM home and refresh"
              className="flex min-w-0 items-center gap-2 rounded-lg px-1.5 py-1 lg:-ml-1.5 text-left transition-colors duration-150 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/60"
            >
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-white/15">
                <CrmMark className="h-4 w-4 text-white" />
              </span>
              <span className="truncate text-[15px] font-semibold leading-none text-white">Lead CRM</span>
            </button>
          </div>
          {/* Leaving the CRM unmounts the softphone and ends any active call —
              guard the only in-app exit with a confirm modal while on a call.
              (Full-page unloads are guarded via beforeunload in SoftphoneContext.) */}
          <div className="flex flex-shrink-0 items-center gap-1">
            <ThemeToggle variant="onColor" />
            <Link
              to="/"
              onClick={(e) => { if (inCall) { e.preventDefault(); setLeaveTo('/'); } }}
              className="inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-2 py-1 text-sm font-medium text-white/90 transition-colors duration-150 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/60"
            >
              <ArrowLeft className="h-4 w-4" />
              Tool Hub
            </Link>
          </div>
        </div>
      </header>

      {leaveTo !== null && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl dark:bg-gray-800">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">End the call and leave?</h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              You're on an active call. Leaving the CRM will end it.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setLeaveTo(null)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Stay on call
              </button>
              <button
                onClick={() => {
                  hangup();
                  const to = leaveTo;
                  setLeaveTo(null);
                  // Logo home does a hard reload; Tool Hub keeps its soft navigate.
                  if (to === '/crm') {
                    window.location.assign('/crm');
                  } else {
                    navigate(to);
                  }
                }}
                className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-red-700"
              >
                Leave &amp; end call
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-stretch min-h-[calc(100vh-3rem)]">
        <Sidebar mobileOpen={mobileNavOpen} onMobileClose={() => setMobileNavOpen(false)} />
        <main className="min-w-0 flex-1 px-4 pt-4 pb-28 sm:px-6 lg:px-8 lg:pt-5 lg:pb-6">
          <div className="mx-auto w-full max-w-[1700px]">{children}</div>
        </main>
      </div>
    </div>
  );
}

/** Every CRM page renders behind the entitlement check inside the shared shell. */
function page(el: ReactNode) {
  return (
    <ProtectedRoute>
      <CRMLayout>{el}</CRMLayout>
    </ProtectedRoute>
  );
}

/** Settings-hub list pages additionally render inside the sub-nav shell. */
function settings(el: ReactNode) {
  return page(<SettingsLayout>{el}</SettingsLayout>);
}

/** Legacy-bookmark redirect that preserves route params and the query string. */
function ParamRedirect({ build }: { build: (params: Readonly<Record<string, string | undefined>>) => string }) {
  const params = useParams();
  const { search } = useLocation();
  return <Navigate to={`${build(params)}${search}`} replace />;
}

function CRMRoutes() {
  const location = useLocation();
  // Record every non-lead CRM location so ContactDetail's back button returns
  // to the page the lead was actually opened from (see rememberLeadOrigin).
  useEffect(() => {
    if (!location.pathname.startsWith('/crm/contacts/')) {
      rememberLeadOrigin(`${location.pathname}${location.search}`);
    }
  }, [location]);
  return (
    <Routes>
      {/* The daily worklist is the landing page: "who do I contact today?" */}
      <Route path="/" element={page(<Today />)} />
      <Route path="/calendar" element={page(<Calendar />)} />
      <Route path="/inbox" element={page(<Contacts mode="inbox" />)} />
      {/* Old bookmark target — contacts triage now lives at /crm/inbox */}
      <Route path="/contacts" element={<Navigate to="/crm/inbox" replace />} />
      <Route path="/active" element={page(<Conversations mode="active" />)} />
      <Route path="/awaiting" element={page(<Conversations mode="awaiting" />)} />
      <Route path="/by-study" element={page(<ByStudy />)} />
      <Route path="/database" element={page(<Contacts mode="database" />)} />
      {/* Settings hub — every config surface under one roof with a sub-nav.
          List pages get the SettingsLayout rail; the full-page builders
          (study hub, form/flow/prompt editors) stay full-width. */}
      <Route path="/settings" element={<Navigate to="/crm/settings/studies" replace />} />
      <Route path="/settings/studies" element={settings(<StudiesList />)} />
      <Route path="/settings/studies/:id" element={page(<StudyDetail />)} />
      <Route path="/settings/forms" element={settings(<FormsList />)} />
      <Route path="/settings/forms/:id" element={page(<FormBuilder />)} />
      <Route path="/settings/flows" element={settings(<FlowsList />)} />
      <Route path="/settings/flows/:id" element={page(<FlowBuilder />)} />
      <Route path="/settings/templates" element={settings(<GlobalTemplates />)} />
      <Route path="/settings/prompts" element={settings(<PromptsList />)} />
      <Route path="/settings/prompts/:key" element={page(<PromptEditor />)} />
      <Route path="/settings/release-content" element={settings(<ReleaseContent />)} />
      <Route path="/settings/registry-backfill" element={settings(<RegistryBackfill />)} />
      <Route path="/settings/system" element={settings(<ConnectionStatus />)} />
      {/* Old bookmark targets — the config pages moved under /crm/settings.
          Param + query preserved (the backend still emits some old-style
          links, e.g. prompt editable_in). */}
      <Route path="/studies" element={<Navigate to="/crm/settings/studies" replace />} />
      <Route path="/studies/:id" element={<ParamRedirect build={(p) => `/crm/settings/studies/${p.id}`} />} />
      <Route path="/forms" element={<Navigate to="/crm/settings/forms" replace />} />
      <Route path="/forms/:id" element={<ParamRedirect build={(p) => `/crm/settings/forms/${p.id}`} />} />
      <Route path="/flows" element={<Navigate to="/crm/settings/flows" replace />} />
      <Route path="/flows/:id" element={<ParamRedirect build={(p) => `/crm/settings/flows/${p.id}`} />} />
      <Route path="/templates" element={<Navigate to="/crm/settings/templates" replace />} />
      <Route path="/prompts" element={<Navigate to="/crm/settings/prompts" replace />} />
      <Route path="/prompts/:key" element={<ParamRedirect build={(p) => `/crm/settings/prompts/${p.key}`} />} />
      <Route path="/release-content" element={<Navigate to="/crm/settings/release-content" replace />} />
      <Route path="/fax" element={page(<FaxConsole />)} />
      {/* Old bookmark targets — records triage + the office directory are now
          sub-tabs of the Fax tab. */}
      <Route path="/records" element={<Navigate to="/crm/fax?tab=unmatched" replace />} />
      <Route path="/provider-offices" element={<Navigate to="/crm/fax?tab=offices" replace />} />
      <Route path="/phone" element={page(<PhoneConsole />)} />
      <Route path="/reports" element={page(<Reports />)} />
      <Route path="/contacts/:id" element={page(<ContactDetail />)} />
      {/* Full results page for one AI eligibility review of this lead. */}
      <Route path="/contacts/:id/reviews/:reviewId" element={page(<EligibilityReviewResults />)} />
      <Route path="/system" element={<Navigate to="/crm/settings/system" replace />} />
      {/* In-app handbooks — readable by all CRM users */}
      <Route path="/help" element={page(<Help />)} />
      {/* Catch-all → CRM home */}
      <Route path="*" element={<Navigate to="/crm" replace />} />
    </Routes>
  );
}

export function CRMApp() {
  return (
    <CRMErrorBoundary>
      <SoftphoneProvider>
        <CallWorkspaceProvider>
          {/* font-inter scopes the CRM's typeface without touching the other
              platform modules; the fixed overlays live inside it so calls and
              toasts render in the same face as the shell. */}
          <div className="font-inter">
            <CRMRoutes />
            {/* Full-screen "Call Mode" workspace — z-40, below the SoftphoneBar;
                auto-opens on connect, mounted once so it survives navigation. */}
            <CallWorkspace />
            {/* Docked call bar — mounted once so a call persists across CRM navigation */}
            <SoftphoneBar />
            {/* Shared parked-calls list (bottom-left, z-40) — pick up from any agent */}
            <ParkedCallsPanel />
            {/* Hang-up rating toast — z-40, deliberately below the SoftphoneBar */}
            <RatingPromptHost />
            {/* Upcoming-call reminders — z-30, below the rating toast and softphone */}
            <CallbackReminderHost />
            {/* Inbound-SMS toasts — bottom-left, z-30; a lead texted the CRM */}
            <InboundSmsToastHost />
          </div>
        </CallWorkspaceProvider>
      </SoftphoneProvider>
    </CRMErrorBoundary>
  );
}

export default CRMApp;
